import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, Edit2, Trash2, Trophy, XCircle, DollarSign, Calendar, User, Building2,
  Clock, Target, FileText, MoreHorizontal, Loader2
} from 'lucide-react'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { pipelineApi, dealApi, usersApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { ActivityTimeline, QuickActivityLog, TaskList, ReminderList, CreateReminderDialog } from '@/components/crm'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export default function DealDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { dealId } = useParams<{ dealId: string }>()

  // State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [wonLostDialogOpen, setWonLostDialogOpen] = useState(false)
  const [wonLostType, setWonLostType] = useState<'won' | 'lost'>('won')
  const [wonLostReason, setWonLostReason] = useState('')

  // Queries
  const { data: deal, isLoading, error } = useQuery({
    queryKey: ['deal', dealId],
    queryFn: () => dealApi.getDeal(dealId!),
    enabled: !!dealId,
  })

  const { data: pipeline } = useQuery({
    queryKey: ['pipeline', deal?.pipeline_id],
    queryFn: () => pipelineApi.getPipeline(deal!.pipeline_id),
    enabled: !!deal?.pipeline_id,
  })

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
  })

  // Mutations
  const moveDealMutation = useMutation({
    mutationFn: ({ stageId }: { stageId: string }) =>
      dealApi.moveDeal(dealId!, { stage_id: stageId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] })
      toast.success(t('crm.dealMoved', 'Deal moved to new stage'))
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  const assignDealMutation = useMutation({
    mutationFn: (assignedTo: string | undefined) =>
      dealApi.assignDeal(dealId!, { assigned_to: assignedTo }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] })
      toast.success(t('crm.dealAssigned', 'Deal assigned'))
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  const deleteDealMutation = useMutation({
    mutationFn: () => dealApi.deleteDeal(dealId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      toast.success(t('messages.deleteSuccess'))
      navigate('/crm/deals')
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.deleteError'))),
  })

  const markWonMutation = useMutation({
    mutationFn: (reason?: string) => dealApi.markWon(dealId!, { won_reason: reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] })
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      toast.success(t('crm.dealMarkedWon', 'Deal marked as won!'))
      setWonLostDialogOpen(false)
      setWonLostReason('')
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  const markLostMutation = useMutation({
    mutationFn: (reason?: string) => dealApi.markLost(dealId!, { lost_reason: reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] })
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      toast.success(t('crm.dealMarkedLost', 'Deal marked as lost'))
      setWonLostDialogOpen(false)
      setWonLostReason('')
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  // Handlers
  const handleMarkWonLost = (type: 'won' | 'lost') => {
    setWonLostType(type)
    setWonLostReason('')
    setWonLostDialogOpen(true)
  }

  const handleWonLostSubmit = () => {
    if (wonLostType === 'won') {
      markWonMutation.mutate(wonLostReason || undefined)
    } else {
      markLostMutation.mutate(wonLostReason || undefined)
    }
  }

  const formatCurrency = (value: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (error || !deal) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <p className="text-destructive">{t('crm.dealNotFound', 'Deal not found')}</p>
        <Button variant="outline" onClick={() => navigate('/crm/deals')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('crm.backToDeals', 'Back to Deals')}
        </Button>
      </div>
    )
  }

  const currentStage = pipeline?.stages?.find(s => s.stage_id === deal.stage_id)

  return (
    <div className="container max-w-5xl py-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/crm/deals')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{deal.deal_name}</h1>
              <Badge
                variant={deal.status === 'WON' ? 'default' : deal.status === 'LOST' ? 'destructive' : 'secondary'}
                className={deal.status === 'WON' ? 'bg-green-500' : ''}
              >
                {deal.status}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              {pipeline?.pipeline_name} • {currentStage?.stage_name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {deal.status === 'OPEN' && (
            <>
              <Button variant="outline" onClick={() => handleMarkWonLost('won')} className="text-green-600">
                <Trophy className="mr-2 h-4 w-4" />
                {t('crm.markWon', 'Mark Won')}
              </Button>
              <Button variant="outline" onClick={() => handleMarkWonLost('lost')} className="text-orange-600">
                <XCircle className="mr-2 h-4 w-4" />
                {t('crm.markLost', 'Mark Lost')}
              </Button>
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/crm/deals/${dealId}/edit`)}>
                <Edit2 className="mr-2 h-4 w-4" />
                {t('common.edit', 'Edit')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)} className="text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                {t('common.delete', 'Delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Deal Value Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-muted-foreground mb-1">
                    <DollarSign className="h-4 w-4" />
                    <span className="text-sm">{t('crm.dealValue', 'Deal Value')}</span>
                  </div>
                  <p className="text-2xl font-bold">{formatCurrency(deal.deal_value, deal.currency)}</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-muted-foreground mb-1">
                    <Target className="h-4 w-4" />
                    <span className="text-sm">{t('crm.probability', 'Probability')}</span>
                  </div>
                  <p className="text-2xl font-bold">{deal.probability}%</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-muted-foreground mb-1">
                    <DollarSign className="h-4 w-4" />
                    <span className="text-sm">{t('crm.weightedValue', 'Weighted Value')}</span>
                  </div>
                  <p className="text-2xl font-bold">{formatCurrency(deal.deal_value * (deal.probability / 100), deal.currency)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {deal.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {t('crm.notes', 'Notes')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap">{deal.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Won/Lost Reason */}
          {(deal.won_reason || deal.lost_reason) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {deal.status === 'WON' ? <Trophy className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-orange-500" />}
                  {deal.status === 'WON' ? t('crm.wonReason', 'Won Reason') : t('crm.lostReason', 'Lost Reason')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap">{deal.won_reason || deal.lost_reason}</p>
              </CardContent>
            </Card>
          )}

          {/* Activity Timeline */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {t('crm.activities', 'Activities')}
              </CardTitle>
              <div className="flex items-center gap-2">
                <CreateReminderDialog entityType="DEAL" entityId={dealId!} />
                <QuickActivityLog entityType="DEAL" entityId={dealId!} />
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <ActivityTimeline entityType="DEAL" entityId={dealId!} showHeader={false} maxItems={5} embedded />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stage Selector */}
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.stage', 'Stage')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={deal.stage_id}
                onValueChange={(value) => moveDealMutation.mutate({ stageId: value })}
                disabled={deal.status !== 'OPEN'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(pipeline?.stages || [])
                    .sort((a, b) => a.display_order - b.display_order)
                    .map((stage) => (
                      <SelectItem key={stage.stage_id} value={stage.stage_id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.stage_color }} />
                          {stage.stage_name}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Details */}
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.details', 'Details')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {deal.expected_close_date && (
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('crm.expectedCloseDate', 'Expected Close')}</p>
                    <p className="font-medium">{format(new Date(deal.expected_close_date), 'MMM d, yyyy')}</p>
                  </div>
                </div>
              )}
              {deal.actual_close_date && (
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('crm.actualCloseDate', 'Actual Close')}</p>
                    <p className="font-medium">{format(new Date(deal.actual_close_date), 'MMM d, yyyy')}</p>
                  </div>
                </div>
              )}
              <Separator />
              {deal.client_name && (
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('crm.client', 'Client')}</p>
                    <p className="font-medium">{deal.client_name}</p>
                  </div>
                </div>
              )}
              {deal.business_name && (
                <div className="flex items-center gap-3">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('crm.business', 'Business')}</p>
                    <p className="font-medium">{deal.business_name}</p>
                  </div>
                </div>
              )}
              <Separator />
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('common.createdAt', 'Created')}</p>
                  <p className="font-medium">{format(new Date(deal.created_at), 'MMM d, yyyy')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assigned To */}
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.assignedTo', 'Assigned To')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={deal.assigned_to || ''}
                onValueChange={(value) => assignDealMutation.mutate(value || undefined)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('common.unassigned', 'Unassigned')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">{t('common.unassigned', 'Unassigned')}</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Tasks */}
          <TaskList entityType="DEAL" entityId={dealId!} />

          {/* Reminders */}
          <ReminderList entityType="DEAL" entityId={dealId!} />
        </div>
      </div>

      {/* Won/Lost Dialog */}
      <Dialog open={wonLostDialogOpen} onOpenChange={setWonLostDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {wonLostType === 'won' ? t('crm.markAsWon', 'Mark as Won') : t('crm.markAsLost', 'Mark as Lost')}
            </DialogTitle>
            <DialogDescription>
              {wonLostType === 'won'
                ? t('crm.wonDescription', 'Congratulations! Add a note about why you won this deal.')
                : t('crm.lostDescription', 'Add a note about why this deal was lost.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reason">
                {wonLostType === 'won' ? t('crm.wonReason', 'Won Reason') : t('crm.lostReason', 'Lost Reason')}
              </Label>
              <Textarea
                id="reason"
                value={wonLostReason}
                onChange={(e) => setWonLostReason(e.target.value)}
                placeholder={wonLostType === 'won' ? t('crm.wonReasonPlaceholder', 'What helped close this deal?') : t('crm.lostReasonPlaceholder', 'Why was this deal lost?')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWonLostDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button
              onClick={handleWonLostSubmit}
              variant={wonLostType === 'won' ? 'default' : 'destructive'}
              disabled={markWonMutation.isPending || markLostMutation.isPending}
            >
              {wonLostType === 'won' ? t('crm.markWon', 'Mark Won') : t('crm.markLost', 'Mark Lost')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('crm.deleteDeal', 'Delete Deal')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('crm.deleteDealConfirmation', 'Are you sure you want to delete this deal? This action cannot be undone.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteDealMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

